// Types for stored data
export interface ScanRecord {
  code: string;
  timestamp: string; // ISO format
  passed: boolean;
  operatorName: string;
  isManualOverride: boolean; // Added to track manual scans
  slotId: number; // NEW: 0, 1, or 2 for slots 1, 2, 3
  sessionId?: string; // NEW: Groups related scans from same session
}

export interface OperatorSession {
  login: string; // ISO format
  logout: string | null; // ISO format, null if session is active
}

export interface Operator {
  name: string;
  lastActive: string; // ISO format
  sessions: OperatorSession[];
}

export interface ManagerData {
  isAuthenticated: boolean;
  lastLogin: string | null; // ISO format
}

export interface CameraSlotConfig {
  deviceId: string | null;
  deviceName: string | null;
  // Enhancement settings per slot
  enhancementEnabled: boolean;
  contrast: number; // default 1
  brightness: number; // default 0
  zoom: number; // default 1
}

export interface CameraConfig {
  slot1: CameraSlotConfig;
  slot2: CameraSlotConfig;
  slot3: CameraSlotConfig;
  lastUpdated: string;
}

export interface AppState {
  version: string;
  currentOperator: string | null;
  cameraConfig: CameraConfig;
}

// NEW: Valid set when all 3 codes match
export interface ValidSet {
  id: string; // unique identifier
  timestamp: string;
  operatorName: string;
  codes: [string, string, string]; // codes from slot 1, 2, 3
  slotIds: [number, number, number]; // always [0, 1, 2]
  sessionId: string;
}

// NEW: Invalid set when codes don't match
export interface InvalidSet {
  id: string; // unique identifier
  timestamp: string;
  operatorName: string;
  codes: [string, string, string]; // codes from slot 1, 2, 3
  slotIds: [number, number, number]; // always [0, 1, 2]
  sessionId: string;
  mismatchDetails: {
    slot1VsSlot2: boolean;
    slot1VsSlot3: boolean;
    slot2VsSlot3: boolean;
  };
}

// Storage keys
export const STORAGE_KEYS = {
  OPERATORS: "lumibot:operators",
  SCAN_HISTORY: "lumibot:scanHistory",
  VALID_SETS: "lumibot:validSets",
  INVALID_SETS: "lumibot:invalidSets",
  MANAGER: "lumibot:manager",
  APP_STATE: "lumibot:appState",
};

// Current version
const CURRENT_VERSION = "1.0";

// Helper functions
const getItem = <T>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error(`Error retrieving ${key} from localStorage:`, error);
    return defaultValue;
  }
};

const setItem = <T>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error storing ${key} in localStorage:`, error);
  }
};

// Initialize storage with default values if not present
const initializeStorage = (): void => {
  // Initialize operators
  if (!localStorage.getItem(STORAGE_KEYS.OPERATORS)) {
    setItem<Operator[]>(STORAGE_KEYS.OPERATORS, []);
  }

  // Initialize scan history
  if (!localStorage.getItem(STORAGE_KEYS.SCAN_HISTORY)) {
    setItem<Record<string, ScanRecord[]>>(STORAGE_KEYS.SCAN_HISTORY, {});
  }

  // Initialize manager data
  if (!localStorage.getItem(STORAGE_KEYS.MANAGER)) {
    setItem<ManagerData>(STORAGE_KEYS.MANAGER, {
      isAuthenticated: false,
      lastLogin: null,
    });
  }

  // Initialize app state
  if (!localStorage.getItem(STORAGE_KEYS.APP_STATE)) {
    setItem<AppState>(STORAGE_KEYS.APP_STATE, {
      version: CURRENT_VERSION,
      currentOperator: null,
      cameraConfig: {
        slot1: { 
          deviceId: null, 
          deviceName: null,
          enhancementEnabled: false,
          contrast: 1,
          brightness: 0,
          zoom: 1
        },
        slot2: { 
          deviceId: null, 
          deviceName: null,
          enhancementEnabled: false,
          contrast: 1,
          brightness: 0,
          zoom: 1
        },
        slot3: { 
          deviceId: null, 
          deviceName: null,
          enhancementEnabled: false,
          contrast: 1,
          brightness: 0,
          zoom: 1
        },
        lastUpdated: new Date().toISOString(),
      },
    });
  }

  // Initialize valid sets
  if (!localStorage.getItem(STORAGE_KEYS.VALID_SETS)) {
    setItem<Record<string, ValidSet[]>>(STORAGE_KEYS.VALID_SETS, {});
  }

  // Initialize invalid sets
  if (!localStorage.getItem(STORAGE_KEYS.INVALID_SETS)) {
    setItem<Record<string, InvalidSet[]>>(STORAGE_KEYS.INVALID_SETS, {});
  }
};

// Operator methods
export const getOperators = (): Operator[] => {
  return getItem<Operator[]>(STORAGE_KEYS.OPERATORS, []);
};

export const setOperators = (operators: Operator[]): void => {
  setItem(STORAGE_KEYS.OPERATORS, operators);
};

export const addOperator = (name: string): void => {
  const operators = getOperators();
  const existingOperator = operators.find((op) => op.name === name);

  const currentTime = new Date().toISOString();

  if (existingOperator) {
    // Update existing operator
    existingOperator.lastActive = currentTime;

    // Check if this operator already has an active session
    const hasActiveSession = existingOperator.sessions.some(
      (session) => session.logout === null
    );

    // Only add a new session if there's no active one
    if (!hasActiveSession) {
      existingOperator.sessions.push({
        login: currentTime,
        logout: null,
      });
    }
  } else {
    // Add new operator
    operators.push({
      name,
      lastActive: currentTime,
      sessions: [
        {
          login: currentTime,
          logout: null,
        },
      ],
    });
  }

  setOperators(operators);
};

export const logoutOperator = (name: string): void => {
  const operators = getOperators();
  const operator = operators.find((op) => op.name === name);

  if (operator) {
    const currentTime = new Date().toISOString();
    operator.lastActive = currentTime;

    // Find all active sessions for this operator and close them
    let sessionClosed = false;

    operator.sessions.forEach((session) => {
      if (session.logout === null) {
        session.logout = currentTime;
        sessionClosed = true;
      }
    });

    // If we actually closed a session, update the operators
    if (sessionClosed) {
      setOperators(operators);
    }
  }
};

// Scan history methods
export const getScanHistory = (): Record<string, ScanRecord[]> => {
  return getItem<Record<string, ScanRecord[]>>(STORAGE_KEYS.SCAN_HISTORY, {});
};

// Helper function to get local date string in YYYY-MM-DD format
const getLocalDateString = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Enhanced scan history methods
export const addScanRecord = (
  record: Omit<ScanRecord, "timestamp" | "slotId" | "sessionId">,
  slotId: number,
  sessionId?: string
): void => {
  const scanHistory = getScanHistory();

  // Create record with timestamp and slot info
  const fullRecord: ScanRecord = {
    ...record,
    timestamp: new Date().toISOString(),
    slotId,
    sessionId,
  };

  // Get today's date in YYYY-MM-DD format for grouping (using local timezone)
  const today = getLocalDateString();

  // Add to today's records
  if (!scanHistory[today]) {
    scanHistory[today] = [];
  }

  scanHistory[today].push(fullRecord);
  setItem(STORAGE_KEYS.SCAN_HISTORY, scanHistory);
};

// NEW: Valid sets methods
export const getValidSets = (): Record<string, ValidSet[]> => {
  return getItem<Record<string, ValidSet[]>>(STORAGE_KEYS.VALID_SETS, {});
};

export const addValidSet = (
  codes: [string, string, string],
  operatorName: string,
  sessionId: string
): void => {
  const validSets = getValidSets();
  const timestamp = new Date().toISOString();
  const today = getLocalDateString(); // Use local date instead of UTC

  const validSet: ValidSet = {
    id: `valid_${timestamp}_${Math.random().toString(36).substr(2, 9)}`,
    timestamp,
    operatorName,
    codes,
    slotIds: [0, 1, 2],
    sessionId,
  };

  if (!validSets[today]) {
    validSets[today] = [];
  }

  validSets[today].push(validSet);
  setItem(STORAGE_KEYS.VALID_SETS, validSets);
};

// NEW: Invalid sets methods
export const getInvalidSets = (): Record<string, InvalidSet[]> => {
  return getItem<Record<string, InvalidSet[]>>(STORAGE_KEYS.INVALID_SETS, {});
};

export const addInvalidSet = (
  codes: [string, string, string],
  operatorName: string,
  sessionId: string
): void => {
  const invalidSets = getInvalidSets();
  const timestamp = new Date().toISOString();
  const today = getLocalDateString(); // Use local date instead of UTC

  const invalidSet: InvalidSet = {
    id: `invalid_${timestamp}_${Math.random().toString(36).substr(2, 9)}`,
    timestamp,
    operatorName,
    codes,
    slotIds: [0, 1, 2],
    sessionId,
    mismatchDetails: {
      slot1VsSlot2: codes[0] !== codes[1],
      slot1VsSlot3: codes[0] !== codes[2],
      slot2VsSlot3: codes[1] !== codes[2],
    },
  };

  if (!invalidSets[today]) {
    invalidSets[today] = [];
  }

  invalidSets[today].push(invalidSet);
  setItem(STORAGE_KEYS.INVALID_SETS, invalidSets);
};

// NEW: Helper method to get scan history by slot
export const getScanHistoryBySlot = (
  slotId: number
): Record<string, ScanRecord[]> => {
  const allHistory = getScanHistory();
  const filteredHistory: Record<string, ScanRecord[]> = {};

  Object.keys(allHistory).forEach((date) => {
    const slotRecords = allHistory[date].filter(
      (record) => record.slotId === slotId
    );
    if (slotRecords.length > 0) {
      filteredHistory[date] = slotRecords;
    }
  });

  return filteredHistory;
};

// Manager methods
export const getManagerData = (): ManagerData => {
  return getItem<ManagerData>(STORAGE_KEYS.MANAGER, {
    isAuthenticated: false,
    lastLogin: null,
  });
};

export const setManagerAuthenticated = (isAuthenticated: boolean): void => {
  const managerData = getManagerData();
  managerData.isAuthenticated = isAuthenticated;

  if (isAuthenticated) {
    managerData.lastLogin = new Date().toISOString();
  }

  setItem(STORAGE_KEYS.MANAGER, managerData);
};

// App state methods
export const getAppState = (): AppState => {
  return getItem<AppState>(STORAGE_KEYS.APP_STATE, {
    version: CURRENT_VERSION,
    currentOperator: null,
    cameraConfig: {
      slot1: { 
        deviceId: null, 
        deviceName: null,
        enhancementEnabled: false,
        contrast: 1,
        brightness: 0,
        zoom: 1
      },
      slot2: { 
        deviceId: null, 
        deviceName: null,
        enhancementEnabled: false,
        contrast: 1,
        brightness: 0,
        zoom: 1
      },
      slot3: { 
        deviceId: null, 
        deviceName: null,
        enhancementEnabled: false,
        contrast: 1,
        brightness: 0,
        zoom: 1
      },
      lastUpdated: new Date().toISOString(),
    },
  });
};

export const setCurrentOperator = (operatorName: string | null): void => {
  const appState = getAppState();
  appState.currentOperator = operatorName;
  setItem(STORAGE_KEYS.APP_STATE, appState);
};

// Camera configuration methods
export const getCameraConfig = (): CameraConfig => {
  const config = getAppState().cameraConfig;
  
  // Migrate old configs that don't have enhancement properties
  const migrateSlot = (slot: CameraSlotConfig): CameraSlotConfig => {
    return {
      deviceId: slot.deviceId,
      deviceName: slot.deviceName,
      enhancementEnabled: slot.enhancementEnabled ?? false,
      contrast: slot.contrast ?? 1,
      brightness: slot.brightness ?? 0,
      zoom: slot.zoom ?? 1,
    };
  };
  
  return {
    slot1: migrateSlot(config.slot1),
    slot2: migrateSlot(config.slot2),
    slot3: migrateSlot(config.slot3),
    lastUpdated: config.lastUpdated,
  };
};

export const setCameraConfig = (cameraConfig: CameraConfig): void => {
  const appState = getAppState();
  appState.cameraConfig = {
    ...cameraConfig,
    lastUpdated: new Date().toISOString(),
  };
  setItem(STORAGE_KEYS.APP_STATE, appState);
};

// Initialize storage when this module is imported
initializeStorage();

// Export the storage service
const StorageService = {
  STORAGE_KEYS,
  getOperators,
  setOperators,
  addOperator,
  logoutOperator,
  getScanHistory,
  addScanRecord,
  getValidSets,
  addValidSet,
  getInvalidSets,
  addInvalidSet,
  getScanHistoryBySlot,
  getManagerData,
  setManagerAuthenticated,
  getAppState,
  setCurrentOperator,
  getCameraConfig,
  setCameraConfig,
};

export default StorageService;
